import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuDownload } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import { generarPDFJunto, fmt } from './PresupuestoPropio';

// Juntar varios presupuestos propios (de distintos clientes y días) en un solo PDF (13-oct-2026):
// hoja de resumen con el total y después cada presupuesto. Antes había que bajar uno por cliente.
const fechaCorta = f => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '');
const ESTADO = { ENVIADO: 'Enviado', ACEPTADO: 'Aceptado', RECHAZADO: 'Rechazado' };
const FILTROS = [
    { id: 'todos', label: 'Todos', pasa: () => true },
    { id: 'ACEPTADO', label: 'Aceptados', pasa: p => p.estado === 'ACEPTADO' },
    { id: 'ENVIADO', label: 'Enviados', pasa: p => p.estado === 'ENVIADO' || !p.estado },
];

export default function JuntarPresupuestos({ cliente = null, onCerrar }) {
    const clienteId = cliente?.id ?? null;
    const [lista, setLista] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [filtro, setFiltro] = useState('todos');
    useEffect(() => {
        // Desde la ficha de un cliente: solo los suyos y ya todos tildados.
        // Si el servidor todavía no tiene /mis-presupuestos (falta actualizar), usa la lista del cliente.
        const listo = (l) => { setLista(l); if (clienteId) setSel(new Set(l.map(p => p.id))); };
        api.get('/mis-presupuestos')
            .then(r => listo((Array.isArray(r.data) ? r.data : []).filter(p => !clienteId || p.clienteId === clienteId)))
            .catch(async () => {
                if (clienteId) {
                    try {
                        const rp = await api.get(`/mis-clientes/${clienteId}/presupuestos`);
                        listo((rp.data || []).map(p => ({ ...p, cliente: { id: clienteId, nombre: cliente.nombre, direccion: cliente.direccion } })));
                        return;
                    } catch { /* sigue abajo */ }
                }
                setLista([]); toast.error('No se pudieron cargar los presupuestos. Probá de nuevo en un rato.');
            });
    }, [clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

    const visibles = useMemo(() => (lista || []).filter(FILTROS.find(f => f.id === filtro).pasa), [lista, filtro]);
    // Agrupados por día, lo más nuevo arriba
    const dias = useMemo(() => {
        const m = new Map();
        visibles.forEach(p => { const k = String(p.fecha).slice(0, 10); if (!m.has(k)) m.set(k, []); m.get(k).push(p); });
        return [...m.entries()];
    }, [visibles]);
    const elegidos = (lista || []).filter(p => sel.has(p.id));
    const total = elegidos.reduce((t, p) => t + (Number(p.total) || 0), 0);

    const alternar = (ids) => setSel(prev => {
        const todos = ids.every(id => prev.has(id));
        const n = new Set(prev); ids.forEach(id => (todos ? n.delete(id) : n.add(id))); return n;
    });
    const bajar = () => {
        if (!elegidos.length) { toast.error('Elegí al menos un presupuesto'); return; }
        generarPDFJunto(elegidos);
    };

    return (
        <ModalShell titulo="Juntar en un PDF" subtitulo="Todo en una hoja: cada presupuesto con su detalle y el total"
            onCerrar={onCerrar} ancho="md:max-w-xl"
            pie={
                <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0 pl-1">
                        <p className="text-caption text-muted">{elegidos.length} elegido{elegidos.length !== 1 ? 's' : ''}</p>
                        <p className="text-body font-black text-ink">{fmt(total)}</p>
                    </div>
                    <button type="button" onClick={bajar} disabled={!elegidos.length}
                        className="h-11 px-5 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center gap-1.5 disabled:opacity-40 active:scale-95">
                        <LuDownload size={15} /> Bajar PDF
                    </button>
                </div>
            }>
            <div className="space-y-3">
                <div className="flex gap-1.5">
                    {FILTROS.map(f => (
                        <button key={f.id} type="button" onClick={() => setFiltro(f.id)}
                            className={`h-8 px-3 rounded-full text-label font-bold border ${filtro === f.id ? 'border-brand-red text-ink bg-[rgba(232,66,47,0.10)]' : 'border-black/10 dark:border-white/10 text-secondary'}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
                {lista === null && <div className="h-24 rounded-2xl bg-panel animate-pulse" />}
                {lista && visibles.length === 0 && <p className="py-8 text-center text-caption text-muted">No hay presupuestos {filtro === 'todos' ? 'guardados todavía' : 'con ese estado'}.</p>}
                {dias.map(([dia, ps]) => {
                    const ids = ps.map(p => p.id);
                    const todos = ids.every(id => sel.has(id));
                    return (
                        <div key={dia}>
                            <div className="flex items-center justify-between px-1 mb-1">
                                <p className="text-label font-black uppercase tracking-widest text-muted">{fechaCorta(dia)}</p>
                                {ps.length > 1 && <button type="button" onClick={() => alternar(ids)} className="text-label font-black text-brand-red">{todos ? 'Ninguno' : 'Todo el día'}</button>}
                            </div>
                            <div className="rounded-2xl bg-panel divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                                {ps.map(p => {
                                    const marcado = sel.has(p.id);
                                    return (
                                        <button key={p.id} type="button" onClick={() => alternar([p.id])} className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
                                            <span className={`w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center text-label font-black ${marcado ? 'bg-brand-red border-brand-red text-white' : 'border-muted'}`}>{marcado ? '✓' : ''}</span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-body font-bold text-ink truncate">{p.cliente?.nombre}</span>
                                                <span className="block text-caption text-muted truncate">N° {p.numero}{p.estado ? ` · ${ESTADO[p.estado] || p.estado}` : ''} · {(p.items || []).map(it => it.desc).join(' · ')}</span>
                                            </span>
                                            <span className="shrink-0 text-body font-black text-ink">{fmt(p.total)}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModalShell>
    );
}
